import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { apiUrl } from "../../../utils/apiUrl";
import {
  createDefaultQuizRounds,
  getMaxQuestionsForRound,
  QUIZ_OPTION_MAX_LEN,
  QUIZ_QUESTION_TEXT_MAX_LEN,
} from "../../../utils/quizTheme";

/** Round line (e.g. "ROUND 1") — displayed in header */
const ROUND_LABEL_MIN = 5;
const ROUND_LABEL_MAX = 50;
/** Round subtitle — longer descriptor under the round line */
const ROUND_SUBTITLE_MIN = 10;
const ROUND_SUBTITLE_MAX = 50;

function buildAssetUrl(base, filename) {
  if (!filename) return "";
  const b = base || "";
  return b + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

function QuestionImageThumb({ src }) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [src]);

  if (!src || imgError) {
    return (
      <div
        className="quiz-q-thumb-placeholder quiz-q-thumb-placeholder--game"
        title="No image yet — upload to add"
        aria-hidden
      >
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect width="32" height="32" rx="6" className="quiz-q-thumb-game-bg" />
          <path
            d="M6 24l7-9 5 6 4-4 7 7H6z"
            className="quiz-q-thumb-game-fg"
          />
          <circle cx="11" cy="11" r="3" className="quiz-q-thumb-game-fg" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="quiz-q-thumb"
      referrerPolicy="no-referrer"
      onError={() => setImgError(true)}
    />
  );
}

function updateQuestion(rounds, roundIdx, qIdx, patch) {
  return rounds.map((round, ri) => {
    if (ri !== roundIdx) return round;
    const questions = (round.questions || []).map((q, qi) =>
      qi === qIdx ? { ...q, ...patch } : q
    );
    return { ...round, questions };
  });
}

/**
 * Full admin UI for theme `rounds`: titles, per-question text/image, options, correct answer, uploads.
 */
export default function QuizRoundsEditor({
  rounds,
  setRounds,
  saving,
  onSave,
  adminToken,
  currentTheme,
  s3Base,
}) {
  const fileInputsRef = useRef({});
  const [activeRoundTab, setActiveRoundTab] = useState(0);

  useEffect(() => {
    setActiveRoundTab((idx) => {
      const max = Math.max(0, rounds.length - 1);
      return Math.min(Math.max(0, idx), max);
    });
  }, [rounds.length]);

  const setRoundLabel = (ri, value) => {
    const clipped = String(value).slice(0, ROUND_LABEL_MAX);
    setRounds((prev) =>
      prev.map((r, i) => (i === ri ? { ...r, roundLabel: clipped } : r))
    );
  };

  const setRoundSubtitle = (ri, value) => {
    const clipped = String(value).slice(0, ROUND_SUBTITLE_MAX);
    setRounds((prev) =>
      prev.map((r, i) => (i === ri ? { ...r, roundSubtitle: clipped } : r))
    );
  };

  const setQuestionField = (ri, qi, field, value) => {
    setRounds((prev) => updateQuestion(prev, ri, qi, { [field]: value }));
  };

  const setOptionText = (ri, qi, oi, value) => {
    const clipped = String(value).slice(0, QUIZ_OPTION_MAX_LEN);
    setRounds((prev) => {
      const q = prev[ri].questions[qi];
      const nextOpts = [...(q.options || ["", "", "", ""])];
      nextOpts[oi] = clipped;
      return updateQuestion(prev, ri, qi, { options: nextOpts });
    });
  };

  const addQuestion = (ri) => {
    const cap = getMaxQuestionsForRound(ri);
    setRounds((prev) => {
      const round = prev[ri];
      const qs = [...(round.questions || [])];
      if (qs.length >= cap) return prev;
      const imageRound = ri >= 1 && ri <= 4;
      qs.push({
        promptType: imageRound ? "image" : "text",
        questionText: "",
        questionImage: "",
        options: ["", "", "", ""],
        correctIndex: 0,
      });
      return prev.map((r, i) => (i === ri ? { ...r, questions: qs } : r));
    });
  };

  const removeQuestion = (ri, qi) => {
    setRounds((prev) => {
      const qs = [...(prev[ri].questions || [])];
      if (qs.length <= 1) return prev;
      qs.splice(qi, 1);
      return prev.map((r, i) => (i === ri ? { ...r, questions: qs } : r));
    });
  };

  const uploadQuestionImage = async (ri, qi, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid file", "Only JPEG / JPG / PNG.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Too large", "Max 5 MB.", "error");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    try {
      const res = await fetch(apiUrl("/uploadCategoryImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");
      setQuestionField(ri, qi, "questionImage", json.filename || "");
      Swal.fire({ icon: "success", title: "Image uploaded", timer: 1400, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Upload error", e.message || "Failed", "error");
    }
  };

  const handleResetTemplate = () => {
    Swal.fire({
      title: "Replace all rounds?",
      text: "This loads the default 6-round layout (1 placeholder question per round, up to 10 each). Your current draft will be lost until you save.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset draft",
    }).then((r) => {
      if (r.isConfirmed) {
        setRounds(createDefaultQuizRounds());
        setActiveRoundTab(0);
      }
    });
  };

  return (
    <div className="quiz-rounds-editor">
      <div className="quiz-rounds-editor-toolbar">
        <p className="quiz-rounds-editor-hint">
          Rounds play in order (Round 1 → Round 6). Use the tabs above to switch rounds. Each round allows 1–10
          questions (you cannot remove the last question in a round). Text questions: up to {QUIZ_QUESTION_TEXT_MAX_LEN}{" "}
          characters; each answer option: up to {QUIZ_OPTION_MAX_LEN}. Round 1 and Round 6 are often text prompts;
          rounds 2–5 often use images.
        </p>
        <div className="quiz-rounds-editor-actions">
          {/* <button
            type="button"
            className="catmgr-btn catmgr-btn-secondary"
            onClick={handleResetTemplate}
          >
            <i className="fa-solid fa-rotate-left" /> Load default layout
          </button> */}
          <button
            type="button"
            className="catmgr-btn catmgr-btn-primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : (
              <>
                <i className="fa-solid fa-floppy-disk" /> Save all rounds
              </>
            )}
          </button>
        </div>
      </div>

      <div className="quiz-round-tabs-wrap">
        <div className="quiz-round-tabs" role="tablist" aria-label="Quiz rounds">
          {rounds.map((_, ri) => {
            const cap = getMaxQuestionsForRound(ri);
            const questions = rounds[ri]?.questions || [];
            const isActive = activeRoundTab === ri;
            return (
              <button
                key={ri}
                type="button"
                role="tab"
                id={`quiz-round-tab-${ri}`}
                aria-selected={isActive}
                aria-controls={`quiz-round-panel-${ri}`}
                tabIndex={isActive ? 0 : -1}
                className={`quiz-round-tab${isActive ? " quiz-round-tab--active" : ""}`}
                onClick={() => setActiveRoundTab(ri)}
              >
                <span className="quiz-round-tab-label">ROUND {ri + 1}</span>
                <span className="quiz-round-tab-count">
                  {questions.length}/{cap}
                </span>
              </button>
            );
          })}
        </div>

        {rounds.map((round, ri) => {
          if (ri !== activeRoundTab) return null;
          const cap = getMaxQuestionsForRound(ri);
          const questions = round.questions || [];
          return (
            <div
              key={ri}
              id={`quiz-round-panel-${ri}`}
              role="tabpanel"
              aria-labelledby={`quiz-round-tab-${ri}`}
              className="quiz-round-block quiz-round-panel"
            >
              <div className="quiz-round-body">
                <div className="quiz-round-title-row quiz-round-title-row--split">
                  <div className="quiz-round-field">
                    <label className="quiz-field-label">Round line</label>
                    <input
                      type="text"
                      className="catmgr-modal-input quiz-round-title-input"
                      value={round.roundLabel || ""}
                      onChange={(e) => setRoundLabel(ri, e.target.value)}
                      placeholder="e.g. ROUND 1"
                      maxLength={ROUND_LABEL_MAX}
                      aria-describedby={`quiz-round-label-hint-${ri}`}
                    />
                    <span
                      id={`quiz-round-label-hint-${ri}`}
                      className={`quiz-field-hint${
                        (round.roundLabel || "").length > 0 &&
                        (round.roundLabel || "").length < ROUND_LABEL_MIN
                          ? " quiz-field-hint--warn"
                          : ""
                      }`}
                    >
                      {ROUND_LABEL_MIN}–{ROUND_LABEL_MAX} characters (current:{" "}
                      {(round.roundLabel || "").length}/{ROUND_LABEL_MAX})
                    </span>
                  </div>
                  <div className="quiz-round-field">
                    <label className="quiz-field-label">Round subtitle</label>
                    <input
                      type="text"
                      className="catmgr-modal-input quiz-round-title-input"
                      value={round.roundSubtitle || ""}
                      onChange={(e) => setRoundSubtitle(ri, e.target.value)}
                      placeholder="e.g. Iconic Dialogues (MCQ)"
                      maxLength={ROUND_SUBTITLE_MAX}
                      aria-describedby={`quiz-round-subtitle-hint-${ri}`}
                    />
                    <span
                      id={`quiz-round-subtitle-hint-${ri}`}
                      className={`quiz-field-hint${
                        (round.roundSubtitle || "").length > 0 &&
                        (round.roundSubtitle || "").length < ROUND_SUBTITLE_MIN
                          ? " quiz-field-hint--warn"
                          : ""
                      }`}
                    >
                      {ROUND_SUBTITLE_MIN}–{ROUND_SUBTITLE_MAX} characters (current:{" "}
                      {(round.roundSubtitle || "").length}/{ROUND_SUBTITLE_MAX})
                    </span>
                  </div>
                </div>

                <div className="quiz-questions-list">
                  {questions.map((q, qi) => (
                    <div key={qi} className="quiz-q-card">
                      <div className="quiz-q-card-head">
                        <span className="quiz-q-card-title">Question {qi + 1}</span>
                        <div className="quiz-q-card-actions">
                          <select
                            className="quiz-prompt-select"
                            value={q.promptType === "image" ? "image" : "text"}
                            onChange={(e) =>
                              setQuestionField(ri, qi, "promptType", e.target.value)
                            }
                          >
                            <option value="text">Text question</option>
                            <option value="image">Image question</option>
                          </select>
                          <button
                            type="button"
                            className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                            title="Remove question"
                            disabled={questions.length <= 1}
                            onClick={() => removeQuestion(ri, qi)}
                          >
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </div>

                      {q.promptType === "image" ? (
                        <div className="quiz-q-image-block">
                          <label className="quiz-field-label">Question image</label>
                          <div className="quiz-q-image-row">
                            <QuestionImageThumb src={buildAssetUrl(s3Base, q.questionImage)} />
                            <input
                              type="text"
                              className="catmgr-modal-input quiz-filename-input"
                              value={q.questionImage || ""}
                              onChange={(e) =>
                                setQuestionField(ri, qi, "questionImage", e.target.value)
                              }
                              placeholder="filename.png (after upload or manual)"
                            />
                            <input
                              type="file"
                              accept=".jpeg,.jpg,.png"
                              className="quiz-file-input"
                              ref={(el) => {
                                fileInputsRef.current[`${ri}-${qi}`] = el;
                              }}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadQuestionImage(ri, qi, f);
                                e.target.value = "";
                              }}
                            />
                            <button
                              type="button"
                              className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                              onClick={() => fileInputsRef.current[`${ri}-${qi}`]?.click()}
                            >
                              Upload
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="quiz-q-text-block">
                          <label className="quiz-field-label">Question text</label>
                          <textarea
                            className="quiz-textarea"
                            rows={3}
                            maxLength={QUIZ_QUESTION_TEXT_MAX_LEN}
                            value={q.questionText || ""}
                            onChange={(e) =>
                              setQuestionField(
                                ri,
                                qi,
                                "questionText",
                                e.target.value.slice(0, QUIZ_QUESTION_TEXT_MAX_LEN)
                              )
                            }
                            placeholder="Enter the question"
                          />
                          <span className="quiz-question-text-length">Characters :{q.questionText.length}/{QUIZ_QUESTION_TEXT_MAX_LEN}</span>
                        </div>
                        
                      )}

                      <label className="quiz-field-label">Answer options (4 choices)</label>
                      <div className="quiz-options-grid">
                        {[0, 1, 2, 3].map((oi) => (
                          <div key={oi} className="quiz-option-row">
                            <span className="quiz-option-letter">{String.fromCharCode(65 + oi)}</span>
                            <input
                              type="text"
                              className="catmgr-modal-input quiz-option-input"
                              maxLength={QUIZ_OPTION_MAX_LEN}
                              value={(q.options && q.options[oi]) || ""}
                              onChange={(e) => setOptionText(ri, qi, oi, e.target.value)}
                              placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="quiz-correct-row">
                        <label className="quiz-field-label">Correct answer</label>
                        <select
                          className="quiz-prompt-select"
                          value={Number(q.correctIndex) || 0}
                          onChange={(e) =>
                            setQuestionField(ri, qi, "correctIndex", parseInt(e.target.value, 10))
                          }
                        >
                          {[0, 1, 2, 3].map((i) => (
                            <option key={i} value={i}>
                              {String.fromCharCode(65 + i)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="catmgr-btn catmgr-btn-add-question"
                  disabled={questions.length >= cap}
                  onClick={() => addQuestion(ri)}
                >
                  <i className="fa-solid fa-plus" /> Add question ({questions.length}/{cap})
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
